import { LockClosedIcon, LinkIcon } from '@heroicons/react/24/outline';
import type { PinSetEntry } from '@/types/versioning';

/**
 * The pin set an endpoint version carries (task 725).
 *
 * The pin set is the lockfile: at publish time the dependency closure is
 * resolved into the exact versions that will serve. Two kinds of line appear,
 * and the distinction is the whole point of this component:
 *
 *   - **Pinned versions** (config, agents, prompts, the generated interface):
 *     frozen, byte-identical to what was published.
 *   - **Live references** (LLM Config, MCP Connection): resolved fresh at every
 *     invocation. Editing one changes what every published version referencing
 *     it does on the next call.
 *
 * Rendering the two identically would let a reader take the pin set for a
 * stronger guarantee than it makes — the report calls this out explicitly as
 * the mitigation for dropping LlmConfig from the versioned set, so the live
 * lines carry their own icon, their own colour, and a sentence saying so.
 */

interface PinSetListProps {
  entries: PinSetEntry[];
  /** Test id prefix so several pin sets can coexist in one panel. */
  testIdPrefix?: string;
}

/** Turn the service's snake_case entity type into something readable. */
const entityLabel = (entityType: string): string =>
  entityType
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export function PinSetList({ entries, testIdPrefix = 'pin-set' }: PinSetListProps) {
  if (entries.length === 0) {
    return (
      <p
        className="text-xs text-gray-500 dark:text-gray-400"
        data-testid={`${testIdPrefix}-empty`}
      >
        This version pins nothing — it has no dependencies to freeze.
      </p>
    );
  }

  const hasLiveReferences = entries.some((entry) => entry.kind === 'reference');

  return (
    <div data-testid={testIdPrefix}>
      <ul className="space-y-1.5">
        {entries.map((entry) => {
          const isLive = entry.kind === 'reference';
          return (
            <li
              key={`${entry.entityType}:${entry.entityId}`}
              data-testid={isLive ? `${testIdPrefix}-live` : `${testIdPrefix}-pinned`}
              className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
                isLive
                  ? 'border-dashed border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-600/60 dark:bg-amber-900/20 dark:text-amber-200'
                  : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300'
              }`}
            >
              {isLive ? (
                <LinkIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              ) : (
                <LockClosedIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              )}
              <span className="min-w-0 flex-1">
                <span className="font-medium">{entry.name || entry.entityId}</span>{' '}
                <span className="opacity-70">({entityLabel(entry.entityType)})</span>
              </span>
              <span className="flex-shrink-0 font-mono">
                {isLive ? 'live reference' : `v${entry.version}`}
              </span>
            </li>
          );
        })}
      </ul>

      {hasLiveReferences && (
        <p
          className="mt-2 text-xs text-gray-500 dark:text-gray-400"
          data-testid={`${testIdPrefix}-live-note`}
        >
          Live references are not frozen. LLM Configs and MCP Connections resolve fresh on every
          call, so editing one changes what this published version does next time it runs.
        </p>
      )}
    </div>
  );
}

export default PinSetList;
